import { FOOD_SUGGEST_MIN_PHRASE_COUNT } from '@cf-shared/food-suggest-interface';
import { splitFoodTextRaw } from '@cf-shared/food-text-tokenize';
// a TYPE-only import on purpose: food-suggest-client imports decideSearchCorrection back,
// and a type import is erased, so the two modules never form a cycle at run time
import type { SuggestionRow } from '@cf-shared/food-search/food-suggestion-row';

/**
 * Deciding whether a committed food search was probably a misspelling, and what to do about it
 * (#5410). A pure module with no Angular in it, so it loads under the node test runner.
 *
 * The design, the measured word pairs and the owner's choice of both numbers are in
 * `design-docs/food-search-suggestions.md`, "Correcting a committed search".
 */

/**
 * How many times commoner the correction must be than what was typed before the app runs the
 * correction INSTEAD of what was typed, rather than merely offering it.
 *
 * 20 comes from Elasticsearch's own published default for this exact situation — a typed word that
 * does exist in the data. Its phrase suggester's `real_word_error_likelihood` defaults to 0.95,
 * documented as "5% of the real words are misspelled": read as odds, a word you can find in the
 * corpus is genuine 19 times out of 20, so overriding what somebody actually typed should beat
 * those odds. It is an anchor rather than a derivation — their 0.95 is a prior probability and this
 * is a frequency comparison — but it lands where the measured pairs say it should: `avacado` is
 * corrected outright at 36.7x, while `brocoli` at 14.3x is only offered, which is right because
 * `brócoli` is the real Spanish word.
 */
export const CORRECT_OUTRIGHT_RATIO = 20;

/**
 * The longest phrase the suggestion index holds. Text longer than this is never a stored phrase,
 * so finding no row for it proves nothing about how common it is — and a correction decision made
 * on that non-fact could reorder somebody's perfectly good four-word search.
 *
 * MIRRORED VALUE — the build stores phrases of 1, 2 and 3 adjacent words
 * (`phrasesOfFood` in `tools/src/food-phrase-index.ts`). Nothing checks that these agree.
 */
export const MAX_PHRASE_WORDS = 3;

/** What the app should do about a committed search that may be misspelled. */
export enum SearchCorrectionKind {
  /** nothing looks wrong: search exactly what was typed and say nothing */
  None = 'None',
  /** offer the correction above the results, but search what was typed */
  Offer = 'Offer',
  /** search the correction instead, and say so with a way back to what was typed */
  Apply = 'Apply',
}

export interface SearchCorrection {
  kind: SearchCorrectionKind;
  /** the corrected line, set for Offer and Apply and empty for None */
  phrase: string;
}

/** The answer when nothing should be said about what was typed. */
export const NO_SEARCH_CORRECTION: SearchCorrection = { kind: SearchCorrectionKind.None, phrase: '' };

/**
 * The words of a line, by the same rule the server and the index use — `splitFoodTextRaw` is what
 * the route sends to Elasticsearch and what the build stored each phrase from. Using anything
 * else here would compare two different notions of "a word".
 */
function wordsOf(text: string): string[] {
  return splitFoodTextRaw(text);
}

/**
 * Compares a typed line with a suggestion row's phrase, as the index would.
 *
 * Splitting both sides is what makes punctuation harmless: somebody who commits `brocoli.` or
 * `grandma's yogurt` must still be matched with the row for the phrase they typed. Getting this
 * wrong is not cosmetic — it would leave the typed line looking absent from the index, and an
 * absent line is treated as rare, which is what decides whether a correction is merely offered or
 * run instead of the search.
 *
 * `splitFoodTextRaw` already lowercases, so case folds here too. Accents are NOT folded: the index
 * deliberately keeps them, so `creme` and `crème` are two phrases with two counts, and treating
 * them as one would attach the wrong count to the text.
 */
function isSameLine(typed: string, phrase: string): boolean {
  const typedWords = wordsOf(typed);
  const phraseWords = wordsOf(phrase);
  return typedWords.length === phraseWords.length
    && typedWords.every((word, at) => word === phraseWords[at]);
}

/** A count that can be compared: a whole number of foods above zero, or nothing usable. */
function usableCount(count: number | undefined): number | undefined {
  return typeof count === 'number' && Number.isFinite(count) && count > 0 ? count : undefined;
}

/**
 * Decide what to do about `typedText`, given the suggestion rows the server answered for exactly
 * that text.
 *
 * The rule, in order:
 *
 * - The best row must be a correction. The server already ranks a mended phrase at its food count
 *   divided by the correction penalty, so a mended phrase reaching first place has already beaten
 *   every spelling of what was typed. When the first row is what was typed, nothing is wrong.
 * - The typed text must be short enough to BE a stored phrase. Beyond that length, no row for it
 *   is expected and its absence says nothing.
 * - How common what was typed is: the count of its own row when the rows carry one, and otherwise
 *   the index's cut — because a phrase the index does not hold appears in fewer foods than that.
 * - Correct outright when the correction is at least `CORRECT_OUTRIGHT_RATIO` times commoner.
 *   Otherwise offer it.
 */
export function decideSearchCorrection(typedText: string, rows: SuggestionRow[]): SearchCorrection {
  const best = rows[0];
  const bestCount = usableCount(best?.count);
  if (!best || !best.mended || bestCount === undefined) {
    return NO_SEARCH_CORRECTION;
  }
  // a correction of what was typed, never of a different search
  if (isSameLine(typedText, best.text)) {
    return NO_SEARCH_CORRECTION;
  }
  const words = wordsOf(typedText).length;
  if (words < 1 || words > MAX_PHRASE_WORDS) {
    return NO_SEARCH_CORRECTION;
  }
  const typedRow = rows.find(row => !row.mended && isSameLine(typedText, row.text));
  // the index holds no phrase below its cut, so a row that is absent -- or one carrying a count
  // no version of the build should ever have written -- bounds the count from above instead
  const typedCount = usableCount(typedRow?.count) ?? FOOD_SUGGEST_MIN_PHRASE_COUNT;
  const kind = bestCount >= CORRECT_OUTRIGHT_RATIO * typedCount
    ? SearchCorrectionKind.Apply
    : SearchCorrectionKind.Offer;
  return { kind, phrase: best.text };
}
